///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2021 - 2025
//                                  RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useDispatch, useSelector } from "react-redux";

import SimpleCell from "./SimpleCell";
import { controls, deleteItem, userRole, status } from "../../../helpers/types";
import Cell from "./Cell";
import { changeCellAndDisableIfRequiredPricing } from "../../../slices/bidPricingSlice";
import { openDeleteConfirmationModal } from "../../../slices/bidSummarySlice";
import DeleteIcon from "../../icons/DeleteIcon";
import navigate from "../../../helpers/navigate";
import addHighlightRowClass from "../../../helpers/addHighlightRowClass";
import removeHighlightRowClass from "../../../helpers/removeHighlightRowClass";
import tableSelection from "../../../helpers/tableSelection";
import Spinner from "../../icons/Spinner";
import ErrorIcon from "../../icons/ErrorIcon";
import {
  fetchMatchingLanes,
  changeCellAndDisableIfRequiredLaneMatch
} from "../../../slices/laneMatchSlice";
import messages from "../../../helpers/messages";
import ButtonCell from "./ButtonCell";
import { fetchLoadHistory } from "../../../slices/analyticsSlice";
import TagCell from "./TagCell";

export const LanesGridRow = ({
  columnOrder,
  lane,
  rowIndex,
  columns,
  role,
  disableDeleteButton,
  matchingLaneRequestLaneId,
  isLaneMatch,
  rowCount,
  numberOfRowsRendered,
  handlePaste,
  userDivision,
  bidDivision,
  analyticsGridView,
  onRowClick,
  selectedRowIndex,
  selectedButtonIndex,
  isCoyoteBid
}) => {
  const columnCount = columns.length;
  const dispatch = useDispatch();
  const { enableInternalCapacityMatching } = useSelector(
    state => state.featureFlags
  );

  const getChildrenForLaneMatchControl = (lane, column) => {
    if (lane["isFetchingLaneCount"]) {
      return (
        <Spinner
          className="spinner"
          fill="#ADAFB2"
          height="16px"
          width="16px"
        />
      );
    } else if (lane["isErrorLaneCount"]) {
      return <ErrorIcon height={16} width={16} fill="#C9CACC" />;
    } else {
      const hasMatches = lane[column.field] === true;

      if (!hasMatches) {
        return null;
      }

      const classNames = [
        "lane-match-count",
        matchingLaneRequestLaneId === lane["truckloadLaneId"]
          ? "lane-match-selected"
          : ""
      ]
        .filter(Boolean)
        .join(" ");

      return (
        <span
          className={classNames}
          onClick={() => {
            dispatch(
              fetchMatchingLanes({
                role,
                truckloadLane: lane,
                resetLaneMatchFilters: false
              })
            );
            dispatch(fetchLoadHistory({ division: bidDivision, lane }));
          }}
        />
      );
    }
  };

  return (
    <tr
      className={`row ${
        selectedRowIndex === lane.metaData.defaultIndex ? "highlight-row" : ""
      } ${
        lane.edited || lane.metaData.laneChanged === true ? "font-bold" : ""
      }`}
      key={lane.metaData.defaultIndex}
      onClick={() =>
        onRowClick &&
        dispatch(
          onRowClick({
            defaultIndex: lane.metaData.defaultIndex,
            analyticsGridView
          })
        )
      }
    >
      {columnOrder.map((co, cellIndex) => {
        const column = columns.find(c => c.field === co);
        const capSourceForIcm =
          column.control === controls.textInput && //Note: While removing toggle, we should set the control as none for Cap Source column and remove this logic
          column.isICM === true &&
          enableInternalCapacityMatching;

        const disabled =
          (column.control === controls.textInput ||
            column.control === controls.none ||
            column.control === controls.tag) &&
          ((isLaneMatch &&
            lane.bidStatus !== status.inProgress &&
            column.field !== "edited" &&
            (role === userRole.tlPricingAnalyst ||
              role === userRole.assetBasedPricingUser)) ||
            lane.metaData.disabled);
        return column.control === controls.none ||
          column.control === controls.laneMatch ||
          (role === userRole.salesRep && isLaneMatch) ||
          capSourceForIcm ||
          disabled ? (
          <SimpleCell
            key={`simple-cell-${column.field}-${rowIndex}`}
            cellDetails={
              column.control === controls.laneMatch
                ? { ...column, format: undefined }
                : column
            }
            value={
              column.control === controls.laneMatch
                ? undefined
                : (column.control === controls.none ||
                    (role === userRole.salesRep && isLaneMatch) ||
                    capSourceForIcm ||
                    disabled) &&
                  lane[column.field]
            }
            rowIndex={rowIndex}
            cellIndex={cellIndex}
            children={
              column.control === controls.laneMatch &&
              getChildrenForLaneMatchControl(lane, column)
            }
            isLaneMatch={isLaneMatch}
            disabled={disabled}
            userDivision={userDivision}
            bidDivision={bidDivision}
            analyticsGridView={analyticsGridView}
            lane={lane}
            role={role}
          />
        ) : column.control === controls.delete ? (
          <SimpleCell
            key={`delete-cell-${column.field}-${rowIndex}`}
            cellDetails={column}
            rowIndex={rowIndex}
            cellIndex={cellIndex}
            role={role}
          >
            <button
              id={rowIndex + "_" + cellIndex}
              data-testid="delete-lane"
              disabled={disableDeleteButton}
              onClick={() =>
                dispatch(
                  openDeleteConfirmationModal({
                    item: lane[column.id],
                    itemType: deleteItem.singleLane
                  })
                )
              }
              onFocus={({ target }) => addHighlightRowClass(target)}
              onBlur={() =>
                removeHighlightRowClass(rowIndex, cellIndex, isLaneMatch)
              }
              onKeyDown={e => navigate(e, rowIndex, cellIndex, columnCount)}
            >
              <DeleteIcon
                height="14px"
                width="12px"
                title={messages.deleteLane.defaultMessage}
                fill={disableDeleteButton ? "#ADAFB2" : "#343434"}
              />
            </button>
          </SimpleCell>
        ) : column.control === controls.button ? (
          <ButtonCell
            key={`button-control-${column.field}-${rowIndex}`}
            column={column}
            rowIndex={rowIndex}
            cellIndex={cellIndex}
            value={lane[column.field]}
            analyticsGridView={analyticsGridView}
            defaultIndex={lane.metaData.defaultIndex}
            selectedButtonIndex={selectedButtonIndex}
          />
        ) : column.control === controls.tag ? (
          <TagCell
            key={`tag-cell-${column.field}-${rowIndex}`}
            cellDetails={column}
            value={lane[column.field]}
            rowIndex={rowIndex}
            cellIndex={cellIndex}
            isLaneMatch={isLaneMatch}
            disabled={disabled}
            userDivision={userDivision}
            bidDivision={bidDivision}
            lane={lane}
          />
        ) : (
          <Cell
            changed={lane.metaData.change[column.field]}
            key={`pricing-cell-${column.field}-${rowIndex}`}
            value={lane[column.field]}
            lane={lane}
            cellDetails={column}
            handleChange={value =>
              dispatch(
                (isLaneMatch
                  ? changeCellAndDisableIfRequiredLaneMatch
                  : changeCellAndDisableIfRequiredPricing)({
                  index: lane.metaData.defaultIndex,
                  field: column.field,
                  value,
                  role,
                  isLaneMatch
                })
              )
            }
            rowIndex={rowIndex}
            cellIndex={cellIndex}
            columnCount={columnCount}
            onMouseDown={e =>
              tableSelection.onMouseDown(e, rowIndex, cellIndex, isLaneMatch)
            }
            onMouseEnter={e =>
              tableSelection.onMouseEnter(e, rowIndex, cellIndex, isLaneMatch)
            }
            onClick={e =>
              tableSelection.handleClick(e, rowIndex, cellIndex, isLaneMatch)
            }
            isLaneMatch={isLaneMatch}
            rowCount={rowCount}
            style={column.style && column.style(lane)}
            onPaste={e =>
              handlePaste(e, rowIndex, numberOfRowsRendered, column)
            }
            disabled={lane.metaData.disabled}
          />
        );
      })}
    </tr>
  );
};

export default React.memo(LanesGridRow);
